import {
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@services/api';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Pagination } from '@material-ui/lab';
import { ContainerButtonsTop, DivSearch } from './styles';
import { NavLink } from '@components/NavLink';

interface IInfos {
  idcad_empresa: number;
  num_cnpj: string;
  nome_empresa: string;
  razao_social: string;
  nome_fantacia: string;
  rua: string;
  numero: string;
  cep: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  nome_contrato: string;
  cod_contrato: string;
  exibir: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const Atua_Empresas: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState<IInfos[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<IInfos | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');
  const [searchValue, setSearchValue] = useState('');

  const PER_PAGE = 30;

  // Carrega todos os dados
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/ravim/empresas-rh`);
        if (res.status === 200) {
          setAllData(res.data);
        }
      } catch (error) {
        console.error(error);
        setSnack(true);
        setMessage('Erro ao carregar empresas');
        setSeverity('error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filtra os dados
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return allData;

    const searchLower = searchValue.toLowerCase();
    return allData.filter(
      (item) =>
        (item.nome_empresa?.toLowerCase() || '').includes(searchLower) ||
        (item.num_cnpj?.toLowerCase() || '').includes(searchLower) ||
        (item.cod_contrato?.toLowerCase() || '').includes(searchLower)
    );
  }, [allData, searchValue]);

  // Paginação
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    return filteredData.slice(startIndex, startIndex + PER_PAGE);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / PER_PAGE);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setCurrentPage(1);
  };

  const deleteContract = (empresa: IInfos) => {
    setEdit(empresa);
    setDialogDelete(true);
  };

  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  const deleteContrato = async () => {
    if (!edit) return;

    try {
      const res = await api.delete(`/ravim/excluEmpresa`, {
        data: { num_cnpj: edit.num_cnpj }
      });

      if (res.status === 200) {
        setSnack(true);
        setMessage(res.data.message);
        setSeverity('success');
        setDialogDelete(false);
        setAllData((prev) =>
          prev.filter((item) => item.num_cnpj !== edit.num_cnpj)
        );
      }
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir empresa');
      setSeverity('error');
    }
  };

  const handleSnackClose = () => {
    setSnack(false);
  };

  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar empresas"
            type="text"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            fullWidth
          />
          {searchValue && (
            <Button
              variantstyle="SECUNDARY"
              icon={<HighlightOff fontSize="medium" />}
              onClick={handleClearSearch}
              style={{ marginLeft: '8px' }}
            />
          )}
        </DivSearch>
      </ContainerButtonsTop>

      <Typography
        variant="h4"
        align="center"
        style={{ marginBottom: 20, fontWeight: 'bold' }}
      >
        Atualizar Contrato de Empresas
      </Typography>

      <Typography variant="h6" align="left" style={{ marginBottom: 16 }}>
        Total de Empresas: {allData.length}
        {searchValue && ` (Filtrados: ${filteredData.length})`}
      </Typography>

      <div
        style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}
      >
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress size={60} />
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table style={{ minWidth: 650 }} aria-label="Tabela de Empresas">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>CNPJ</strong>
                </TableCell>
                <TableCell>
                  <strong>Nome Comercial</strong>
                </TableCell>
                <TableCell>
                  <strong>Código Contrato</strong>
                </TableCell>
                <TableCell>
                  <strong>Cidade/Estado</strong>
                </TableCell>
                <TableCell>
                  <strong>ATUALIZAR</strong>
                </TableCell>
                <TableCell>
                  <strong>DELETAR</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  <TableRow key={item.idcad_empresa}>
                    <TableCell>{item.num_cnpj}</TableCell>
                    <TableCell>{item.nome_empresa}</TableCell>
                    <TableCell>{item.cod_contrato}</TableCell>
                    <TableCell>{`${item.cidade} - ${item.estado}`}</TableCell>
                    <TableCell>
                      <Link
                        to={{ pathname: '/novo-contr-empresas', state: item }}
                      >
                        <IconButton aria-label="editar">
                          <SettingsBackupRestore
                            fontSize="medium"
                            style={{ color: '#422C59' }}
                          />
                        </IconButton>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="deletar"
                        onClick={() => deleteContract(item)}
                      >
                        <HighlightOff
                          fontSize="medium"
                          style={{ color: 'red' }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {searchValue
                      ? `Nenhum resultado encontrado para "${searchValue}"`
                      : 'Nenhum registro encontrado'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </div>

      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Você está prestes a excluir a seguinte empresa:
          </Typography>
          <Typography>
            <strong>Nome:</strong> {edit?.nome_empresa}
          </Typography>
          <Typography>
            <strong>CNPJ:</strong> {edit?.num_cnpj}
          </Typography>
          <Typography color="error" style={{ marginTop: 16 }}>
            Esta ação não pode ser desfeita. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDialog}
            title="Cancelar"
            variantstyle="SECUNDARY"
          />
          <Button
            onClick={deleteContrato}
            title="Confirmar Exclusão"
            variantstyle="RED"
            autoFocus
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
export default Atua_Empresas;
